import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Check, Crown, Loader2, X, Gamepad2 } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const FREE_FEATURES = [
  { t: "Limited free plays", ok: true },
  { t: "Access to all games & quizzes", ok: true },
  { t: "Save scores when logged in", ok: true },
  { t: "Ad-supported experience", ok: false },
  { t: "Unlimited plays", ok: false },
];

const PREMIUM_FEATURES = [
  "Zero ads, ever",
  "Unlimited plays on every game",
  "Save scores to the leaderboard",
  "Priority access to new games",
  "Support indie game development",
];

export default function Subscribe() {
  const { user, setShowAuth, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const polled = useRef(false);

  useEffect(() => {
    // PayPal appends its own ?token=<order id> to the return URL — it does not
    // substitute a placeholder the way Stripe does with {CHECKOUT_SESSION_ID},
    // and it rejects a return_url containing braces outright. session_id is
    // still read first so any older link keeps working.
    const sessionId = params.get("session_id") || params.get("token");
    if (sessionId && !polled.current) {
      polled.current = true;
      setPolling(true);
      pollStatus(sessionId, 0);
    }
  }, [params]);

  const pollStatus = async (sessionId, attempts) => {
    if (attempts >= 6) {
      setPolling(false);
      toast.error("Payment status check timed out. If charged, your premium will activate shortly.");
      return;
    }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setPolling(false);
        toast.success("You're Premium now! Enjoy the ad-free Mania. 👑");
        await checkAuth();
        setParams({});
        return;
      }
      if (data.status === "expired") {
        setPolling(false);
        toast.error("Payment session expired. Please try again.");
        return;
      }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch {
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    }
  };

  const subscribe = async () => {
    if (!user) { setShowAuth(true); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: "premium_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch {
      toast.error("Couldn't start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-12" data-testid="subscribe-page">
      <Seo title="Go Premium — Ad-Free Gaming" description="Upgrade to Video Game Mania Premium for an ad-free experience, unlimited plays, and leaderboard glory. Just $4.99/month." />

      {polling && (
        <div className="mb-8 flex items-center justify-center gap-2 text-neon-cyan font-mono" data-testid="payment-polling">
          <Loader2 className="w-5 h-5 animate-spin" /> Confirming your payment…
        </div>
      )}

      <div className="text-center mb-12">
        <Crown className="w-12 h-12 text-neon-yellow mx-auto mb-3" />
        <h1 className="font-display font-black text-4xl md:text-6xl uppercase tracking-tighter">Go Premium</h1>
        <p className="text-zinc-400 mt-3 max-w-xl mx-auto">Play more, ad-free. A 30-day pass — renew whenever you like. One simple plan.</p>
      </div>

      {user?.is_premium ? (
        <div className="max-w-md mx-auto text-center bg-zinc-950 border-2 border-neon-lime rounded-2xl p-10" data-testid="already-premium">
          <Crown className="w-12 h-12 text-neon-yellow mx-auto mb-3" />
          <h2 className="font-display font-black text-2xl uppercase">You're Premium! 🎉</h2>
          <p className="text-zinc-400 mt-2">Enjoy unlimited, ad-free gaming.</p>
          {user.premium_until && (
            <p className="text-sm text-zinc-300 mt-3 font-mono" data-testid="premium-until">
              Active until {new Date(user.premium_until).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
            </p>
          )}
          <div className="flex flex-col gap-3 mt-6">
            <Button onClick={subscribe} disabled={loading} data-testid="renew-premium-btn"
              className="bg-neon-pink text-white rounded-none font-bold uppercase tracking-widest hover:bg-white hover:text-black">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Crown className="w-4 h-4 mr-2" /> Renew · +30 days</>}
            </Button>
            <Button onClick={() => navigate("/arcade")} variant="outline" className="border-zinc-700 text-zinc-200 bg-transparent rounded-none font-bold uppercase tracking-widest hover:border-neon-cyan">
              <Gamepad2 className="w-4 h-4 mr-2" /> Start Playing
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Free */}
          <div className="bg-zinc-950 border-2 border-zinc-800 rounded-2xl p-8">
            <h3 className="font-display font-black text-2xl uppercase tracking-tight">Free Player</h3>
            <p className="font-display font-black text-4xl mt-2">$0<span className="text-base text-zinc-500 font-sans">/forever</span></p>
            <ul className="mt-6 space-y-3">
              {FREE_FEATURES.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  {f.ok ? <Check className="w-4 h-4 text-neon-lime shrink-0" /> : <X className="w-4 h-4 text-zinc-600 shrink-0" />}
                  <span className={f.ok ? "text-zinc-300" : "text-zinc-600 line-through"}>{f.t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 text-center text-zinc-500 font-mono text-sm py-3 border border-zinc-800 rounded">Current plan</div>
          </div>

          {/* Premium */}
          <div className="relative bg-zinc-950 border-2 border-neon-pink rounded-2xl p-8 overflow-hidden"
            style={{ boxShadow: "0 0 40px rgba(255,0,60,0.25)" }}>
            <div className="absolute top-0 right-0 bg-neon-pink text-white text-xs font-bold uppercase tracking-widest px-3 py-1">Most Popular</div>
            <h3 className="font-display font-black text-2xl uppercase tracking-tight flex items-center gap-2">
              <Crown className="w-6 h-6 text-neon-yellow" /> Mania Premium
            </h3>
            <p className="font-display font-black text-4xl mt-2">$4.99<span className="text-base text-zinc-500 font-sans">/month</span></p>
            <ul className="mt-6 space-y-3">
              {PREMIUM_FEATURES.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-neon-lime shrink-0" /><span className="text-zinc-200">{f}</span>
                </li>
              ))}
            </ul>
            <Button onClick={subscribe} disabled={loading} data-testid="subscribe-premium-btn"
              className="w-full mt-8 bg-neon-pink text-white rounded-none font-bold uppercase tracking-widest h-12 hover:bg-white hover:text-black">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{user ? "Subscribe Now" : "Log In to Subscribe"}</>}
            </Button>
            <p className="text-center text-xs text-zinc-500 mt-3">Secure checkout via Stripe</p>
          </div>
        </div>
      )}
    </div>
  );
}
